/**
 * Encrypts passwords in the browser before they are sent (VAPT 2026-10-16,
 * finding 2: passwords were readable in the request body by anything that
 * can see the request, such as a proxy that terminates TLS).
 *
 * Same as apps/web/src/lib/credentialEncryption.ts; mirrors
 * bff/src/modules/credential-encryption/credential-key.service.ts:
 * a random AES-256-GCM key encrypts {"p": password, "ts": now, "n": nonce},
 * with the field name as additional data; the BFF's RSA-OAEP-SHA256 public
 * key wraps the AES key. The field becomes
 * `enc:v1:<kid>:<wrappedKey>.<iv>.<ciphertext>` (base64url parts).
 *
 * `ts` is in server time: the key endpoint returns the BFF's clock, and the
 * difference from this device's clock is applied. The BFF rejects a `ts` more
 * than two minutes off, so a device whose clock is wrong could not sign in.
 *
 * The BFF rejects plain passwords. If it has no key endpoint yet (an older
 * BFF during a deploy), this sends the password as before, which that BFF
 * still accepts.
 */

// Same base URL as api-client.ts; fetched without its interceptors because
// login and reset pages call this before anyone is signed in.
const BFF_URL = import.meta.env.VITE_BFF_URL || '/api';
// Re-read the key at least this often; the BFF accepts each key for 48h.
const KEY_CACHE_MS = 5 * 60 * 1000;

export const PASSWORD_FIELDS = ['password', 'newPassword', 'currentPassword'] as const;

// BFF codes that a fresh key and a new timestamp fix: the key rotated away or
// the BFF pod doesn't know it, or the timestamp drifted (e.g. the device clock
// changed while the key was cached).
const RETRYABLE_CODES = new Set(['CREDENTIAL_KEY_UNKNOWN', 'CREDENTIAL_EXPIRED']);

/** Thrown when passwords can't be encrypted; `message` is for the user. */
export class CredentialEncryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CredentialEncryptionError';
  }
}

interface PublicCredentialKey {
  kid: string;
  publicKey: string; // SPKI DER, base64
  serverTime?: number; // BFF clock, epoch ms; absent on older BFFs
}

let cached: { kid: string; key: CryptoKey; fetchedAt: number; clockOffset: number } | null | undefined;

const b64u = (bytes: ArrayBuffer | Uint8Array): string => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  for (const b of arr) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromB64 = (s: string): ArrayBuffer => Uint8Array.from(atob(s), (c) => c.charCodeAt(0)).buffer as ArrayBuffer;

/** WebCrypto, or a user-facing error when this page can't use it. */
function subtleCrypto(): SubtleCrypto {
  // Browsers only expose crypto.subtle on https:// and localhost pages.
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new CredentialEncryptionError(
      globalThis.isSecureContext === false
        ? 'This page can’t sign you in securely because it was opened over http://. Open the https:// address and try again.'
        : 'This browser can’t sign you in securely. Update it to the latest version and try again.',
    );
  }
  return subtle;
}

/** The BFF's current key, or null when the BFF predates encryption. */
async function getKey(): Promise<{ kid: string; key: CryptoKey; clockOffset: number } | null> {
  if (cached && Date.now() - cached.fetchedAt < KEY_CACHE_MS) return cached;
  const subtle = subtleCrypto();
  const sentAt = Date.now();
  const res = await fetch(`${BFF_URL}/auth/credential-key`, { cache: 'no-store' });
  const receivedAt = Date.now();
  if (res.status === 404) return null;
  if (!res.ok) throw new CredentialEncryptionError('Could not prepare a secure sign-in. Please try again.');
  const body = (await res.json()) as PublicCredentialKey;
  const key = await subtle.importKey(
    'spki',
    fromB64(body.publicKey),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
  // The server read its clock about halfway through the round trip.
  const clockOffset = typeof body.serverTime === 'number' ? body.serverTime - Math.round((sentAt + receivedAt) / 2) : 0;
  cached = { kid: body.kid, key, fetchedAt: receivedAt, clockOffset };
  return cached;
}

// crypto.randomUUID needs a newer browser than the rest of this file.
const nonce = (): string => b64u(crypto.getRandomValues(new Uint8Array(16)));

async function encryptValue(
  rsa: { kid: string; key: CryptoKey; clockOffset: number },
  field: string,
  value: string,
): Promise<string> {
  const subtle = subtleCrypto();
  const aes = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const wrapped = await subtle.encrypt({ name: 'RSA-OAEP' }, rsa.key, await subtle.exportKey('raw', aes));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const payload = JSON.stringify({ p: value, ts: Date.now() + rsa.clockOffset, n: nonce() });
  const ct = await subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(field) },
    aes,
    new TextEncoder().encode(payload),
  );
  return `enc:v1:${rsa.kid}:${b64u(wrapped)}.${b64u(iv)}.${b64u(ct)}`;
}

/**
 * A copy of `body` with every password field encrypted. Other fields, and
 * empty passwords, are left as they are.
 */
export async function encryptPasswords<T extends object>(body: T): Promise<T> {
  const fields = PASSWORD_FIELDS.filter((f) => {
    const v = (body as Record<string, unknown>)[f];
    return typeof v === 'string' && v !== '';
  });
  if (fields.length === 0) return body;

  const rsa = await getKey();
  if (!rsa) return body;

  const out = { ...body } as Record<string, unknown>;
  for (const f of fields) out[f] = await encryptValue(rsa, f, out[f] as string);
  return out as T;
}

/** The BFF error code in a rejected axios call or a fetch Response, if any. */
async function errorCode(outcome: unknown): Promise<string | undefined> {
  if (typeof Response !== 'undefined' && outcome instanceof Response) {
    if (outcome.status !== 400) return undefined;
    const data = await outcome.clone().json().catch(() => null);
    return typeof data?.code === 'string' ? data.code : undefined;
  }
  const res = (outcome as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  return res?.status === 400 && typeof res.data?.code === 'string' ? res.data.code : undefined;
}

/**
 * Encrypts `body`'s passwords and sends it with `send` (an axios call or a
 * fetch). If the BFF says the key or timestamp was stale, fetches a fresh key
 * and sends once more with a newly encrypted body; a ciphertext is never
 * sent twice, since the BFF rejects a reused nonce.
 */
export async function sendWithEncryptedPasswords<T extends object, R>(
  body: T,
  send: (encrypted: T) => Promise<R>,
): Promise<R> {
  const retry = () => {
    resetCredentialKeyCache();
    return encryptPasswords(body).then(send);
  };
  let result: R;
  try {
    result = await send(await encryptPasswords(body));
  } catch (err) {
    if (RETRYABLE_CODES.has((await errorCode(err)) ?? '')) return retry();
    throw err;
  }
  if (RETRYABLE_CODES.has((await errorCode(result)) ?? '')) return retry();
  return result;
}

/** Test hook: forget the cached key. */
export function resetCredentialKeyCache(): void {
  cached = undefined;
}
